import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiFetch } from "../api";

const COLORS = {
  yellow: "#E6FF3E",
  gray: "#94a3b8",
  red: "#f87171",
  green: "#4ade80",
  orange: "#fb923c",
  blue: "#60a5fa",
  teal: "#0F9D9D",
  purple: "#a78bfa",
  white: "#e2e8f0",
  grid: "#1e3a50",
};

const DEFAULT_LIMIT = 100;

const SLICE_OPTIONS = [
  { key: 100, label: "100" },
  { key: 200, label: "200" },
  { key: 300, label: "300" },
  { key: 400, label: "400" },
  { key: 500, label: "500" },
  { key: "all", label: "Tout" },
];

function limitLabel(key) {
  if (key === "all") return "tous les plis";
  return `${key} derniers plis`;
}

function limitQuery(key) {
  return key === "all" ? "all" : String(key);
}

function buildApiQuery(sliceLimit, filterReceptionPn) {
  const qs = new URLSearchParams({ limit: limitQuery(sliceLimit) });
  if (filterReceptionPn && filterReceptionPn !== "Tous") {
    qs.set("pn", filterReceptionPn);
  }
  return qs.toString();
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAxisDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function formatValue(v, decimals = 2, unit = "") {
  if (v == null || Number.isNaN(v)) return "—";
  return `${Number(v).toFixed(decimals)}${unit}`;
}

function chartRefLines(config) {
  if (config.refLines?.length) return config.refLines;
  if (config.refLine != null) {
    return [{ value: config.refLine, label: config.refLineLabel, color: COLORS.yellow }];
  }
  return [];
}

function refLineLabelPosition(ref, index, total) {
  if (ref.labelPosition) return ref.labelPosition;
  if (total > 1) return index === 0 ? "left" : "right";
  return "right";
}

function RefLineLabel({ viewBox, value, fill, labelPosition = "right" }) {
  if (!viewBox || value == null || value === "") return null;
  const { x = 0, y = 0, width = 0 } = viewBox;
  const text = String(value);
  const fontSize = 10;
  const padX = 6;
  const padY = 3;
  const textWidth = Math.max(text.length * 6.5 + padX * 2, 34);
  const textHeight = fontSize + padY * 2;
  const isLeft = labelPosition === "left";
  const rectX = isLeft ? x + 8 : x + width - textWidth - 8;
  const rectY = y - textHeight / 2;
  const color = fill || COLORS.yellow;

  return (
    <g>
      <rect
        x={rectX}
        y={rectY}
        width={textWidth}
        height={textHeight}
        rx={4}
        fill="rgba(4, 30, 46, 0.94)"
        stroke={color}
        strokeWidth={1}
      />
      <text
        x={rectX + textWidth / 2}
        y={rectY + textHeight - padY - 1}
        textAnchor="middle"
        fill={color}
        fontSize={fontSize}
        fontWeight={700}
      >
        {text}
      </text>
    </g>
  );
}

export const HUMIDITE_BANC_CONFIG = {
  apiPath: "/api/dashboard/logistique/humidite-banc",
  title: "💧 Humidité des plis — Banc de test",
  columnLabel: "humidite_moyenne",
  yAxisLabel: "Humidité %",
  unit: "%",
  decimals: 2,
  lineColor: COLORS.teal,
  refLine: 30,
  refLineLabel: "30",
  alertSummaryKey: "sup_30",
  alertKpiLabel: "> 30 %",
  alertHoverTitle: "Principaux plis > 30 %",
  alertUnit: " %",
  medianSub: "mesures ≤ 30 %",
  loadingMsg: "Chargement humidité banc de test…",
  errorMsg: "Erreur chargement humidité banc",
  legend: [
    { color: COLORS.green, label: "OK" },
    { color: COLORS.red, label: "> 30 %" },
    { color: COLORS.orange, label: "< 1 %" },
    { color: COLORS.yellow, label: "Seuil 30 %", line: true },
  ],
  statusLabel: (s) => {
    if (s === "sup_30") return "> 30 %";
    if (s === "trop_bas") return "< 1 %";
    if (s === "neg_zero") return "Nulle / négative";
    return "OK";
  },
  statusColor: (s) => {
    if (s === "ok") return COLORS.green;
    if (s === "sup_30") return COLORS.red;
    if (s === "trop_bas") return COLORS.orange;
    return COLORS.red;
  },
  isAlertPoint: (m) => m.over_max || m.status !== "ok",
};

export const DENSITE_BANC_CONFIG = {
  apiPath: "/api/dashboard/logistique/densite-banc",
  title: "📊 Densité anhydre — Banc de test",
  columnLabel: "Densite_anhydre",
  yAxisLabel: "Densité anhydre",
  unit: "",
  decimals: 4,
  lineColor: COLORS.purple,
  refLine: 1,
  refLineLabel: "1",
  alertSummaryKey: "invalides",
  alertKpiLabel: "Invalides",
  alertHoverTitle: "Plis avec densité invalide",
  alertUnit: "",
  medianSub: "mesures valides (0 < d ≤ 1)",
  loadingMsg: "Chargement densité anhydre banc…",
  errorMsg: "Erreur chargement densité banc",
  legend: [
    { color: COLORS.green, label: "OK" },
    { color: COLORS.red, label: "> 1" },
    { color: COLORS.orange, label: "≤ 0" },
    { color: COLORS.yellow, label: "Seuil 1", line: true },
  ],
  statusLabel: (s) => {
    if (s === "sup_1") return "> 1";
    if (s === "neg_zero") return "Nulle / négative";
    return "OK";
  },
  statusColor: (s) => {
    if (s === "ok") return COLORS.green;
    if (s === "neg_zero") return COLORS.orange;
    return COLORS.red;
  },
  isAlertPoint: (m) => m.over_max || m.status !== "ok",
};

export const LONGUEUR_BANC_CONFIG = {
  apiPath: "/api/dashboard/logistique/longueur-banc",
  title: "📏 Longueur moyenne — Banc de test",
  columnLabel: "Longueur_moyenne",
  yAxisLabel: "Longueur (mm)",
  unit: " mm",
  decimals: 1,
  lineColor: COLORS.blue,
  refLine: null,
  refLineLabel: null,
  refLines: [
    { value: 2150, label: "2150", color: COLORS.yellow, labelPosition: "left" },
    { value: 2350, label: "2350", color: COLORS.yellow, labelPosition: "right" },
  ],
  alertSummaryKey: "invalides",
  alertKpiLabel: "Hors tolérance",
  alertHoverTitle: "Plis hors plage 2150–2350 mm",
  alertUnit: " mm",
  medianSub: "mesures dans la plage 2150–2350 mm",
  loadingMsg: "Chargement longueur banc…",
  errorMsg: "Erreur chargement longueur banc",
  legend: [
    { color: COLORS.green, label: "OK" },
    { color: COLORS.red, label: "> 2350 mm" },
    { color: COLORS.orange, label: "< 2150 mm" },
    { color: COLORS.yellow, label: "Seuils", line: true },
  ],
  statusLabel: (s) => {
    if (s === "sup_max") return "> 2350 mm";
    if (s === "trop_bas") return "< 2150 mm";
    if (s === "neg_zero") return "Nulle / négative";
    return "OK";
  },
  statusColor: (s) => {
    if (s === "ok") return COLORS.green;
    if (s === "trop_bas") return COLORS.orange;
    if (s === "neg_zero") return COLORS.orange;
    return COLORS.red;
  },
  isAlertPoint: (m) => m.status !== "ok",
};

function MesureDot({ cx, cy, payload, config }) {
  if (cx == null || cy == null || !payload) return null;
  const alert = config.isAlertPoint(payload);
  return (
    <circle
      cx={cx}
      cy={cy}
      r={alert ? 5 : 3.5}
      fill={config.statusColor(payload.status)}
      stroke="#041e2e"
      strokeWidth={1.5}
    />
  );
}

function MesureTooltip({ active, payload, config }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: "#071e2f",
      border: `1px solid ${config.statusColor(d.status)}`,
      borderRadius: 10,
      padding: "10px 12px",
      fontSize: 12,
      color: COLORS.white,
      boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    }}>
      <div style={{ fontFamily: "monospace", fontWeight: 800, marginBottom: 4 }}>{d.lot}</div>
      <div style={{ color: COLORS.gray, fontSize: 10, marginBottom: 4 }}>plyPn · banc de test</div>
      <div style={{ color: config.statusColor(d.status), fontWeight: 800, fontSize: 15 }}>
        {formatValue(d.valeur, config.decimals, config.unit)}
      </div>
      <div style={{ color: COLORS.gray, marginTop: 4 }}>{config.statusLabel(d.status)}</div>
      <div style={{ color: COLORS.gray, marginTop: 2, fontSize: 11 }}>{formatDate(d.date)}</div>
    </div>
  );
}

function KpiMini({ label, value, sub, color, hoverLots, config }) {
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  return (
    <>
      <div
        style={{
          ...S.kpiMini,
          borderTopColor: color,
          cursor: hoverLots?.length ? "help" : "default",
        }}
        onMouseEnter={(e) => {
          if (hoverLots?.length) {
            setHover(true);
            setPos({ x: e.clientX, y: e.clientY });
          }
        }}
        onMouseMove={(e) => hoverLots?.length && setPos({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => setHover(false)}
      >
        <div style={{ ...S.kpiMiniValue, color }}>{value ?? "—"}</div>
        <div style={S.kpiMiniLabel}>{label}</div>
        {sub && <div style={S.kpiMiniSub}>{sub}</div>}
      </div>
      {hover && hoverLots?.length > 0 && (
        <div style={{
          position: "fixed",
          left: Math.min(pos.x + 12, window.innerWidth - 300),
          top: Math.min(pos.y + 12, window.innerHeight - 220),
          zIndex: 9999,
          background: "#071e2f",
          border: `1px solid ${COLORS.red}`,
          borderRadius: 10,
          padding: "10px 12px",
          fontSize: 12,
          color: COLORS.white,
          pointerEvents: "none",
          minWidth: 220,
          maxWidth: 280,
        }}>
          <div style={{ color: COLORS.red, fontWeight: 800, marginBottom: 6 }}>{config.alertHoverTitle}</div>
          {hoverLots.map((x) => (
            <div key={x.lot} style={{ marginBottom: 4, fontSize: 11 }}>
              <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{x.lot}</span>
              <span style={{ color: COLORS.gray }}>
                {" "}· {x.count} mes. · max {formatValue(x.max_v, config.decimals, config.alertUnit)}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function BancMesureSection({ config, lots = [], filterEss = "Toutes", filterFourn = "Tous" }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [sliceLimit, setSliceLimit] = useState(DEFAULT_LIMIT);
  const [filterReceptionPn, setFilterReceptionPn] = useState("Tous");
  const [lotSearch, setLotSearch] = useState("");

  const receptionLotsAll = useMemo(
    () =>
      lots
        .filter((l) => Boolean(l.PN))
        .sort((a, b) => String(a.PN).localeCompare(String(b.PN))),
    [lots]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch(`${config.apiPath}?${buildApiQuery(sliceLimit, filterReceptionPn)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.status === "error") throw new Error(json.message || config.errorMsg);
        if (!cancelled) setPayload(json);
      } catch (e) {
        if (!cancelled) setError(e.message || config.errorMsg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [config.apiPath, config.errorMsg, sliceLimit, filterReceptionPn]);

  useEffect(() => {
    setLotSearch("");
  }, [sliceLimit]);

  useEffect(() => {
    setFilterReceptionPn("Tous");
    setLotSearch("");
  }, [filterEss, filterFourn]);

  const receptionLots = useMemo(
    () =>
      receptionLotsAll.filter((l) => {
        if (filterEss !== "Toutes" && l.Essence !== filterEss) return false;
        if (filterFourn !== "Tous" && l.Fournisseur !== filterFourn) return false;
        return true;
      }),
    [receptionLotsAll, filterEss, filterFourn]
  );

  const filteredMesures = useMemo(() => {
    const recs = payload?.recent_mesures || [];
    return recs.filter((r) => {
      const pn = r.reception_pn;
      if (!pn) return filterEss === "Toutes" && filterFourn === "Tous";
      const lot = receptionLotsAll.find((l) => l.PN === pn);
      if (!lot) return filterEss === "Toutes" && filterFourn === "Tous";
      if (filterEss !== "Toutes" && lot.Essence !== filterEss) return false;
      if (filterFourn !== "Tous" && lot.Fournisseur !== filterFourn) return false;
      return true;
    });
  }, [payload, receptionLotsAll, filterEss, filterFourn]);

  const lotsForSelect = useMemo(() => {
    const q = lotSearch.trim().toLowerCase();
    if (!q) return receptionLots;
    return receptionLots.filter((lot) => {
      const hay = [lot.PN, lot["Num de lot"], lot.Essence, lot.Fournisseur, lot.Statut]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [receptionLots, lotSearch]);

  useEffect(() => {
    if (filterReceptionPn !== "Tous" && !receptionLots.some((l) => l.PN === filterReceptionPn)) {
      setFilterReceptionPn("Tous");
    }
  }, [receptionLots, filterReceptionPn]);

  const selectedReceptionLot = useMemo(
    () => receptionLots.find((l) => l.PN === filterReceptionPn) || null,
    [receptionLots, filterReceptionPn]
  );

  const alertSummary = payload?.summary?.[config.alertSummaryKey] || {};

  const chartData = useMemo(
    () =>
      [...filteredMesures]
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
        .map((m, i) => ({
          idx: i + 1,
          valeur: m.v,
          lot: m.lot,
          date: m.date,
          status: m.status,
          over_max: m.over_max,
          axisLabel: formatAxisDate(m.date) || String(i + 1),
        })),
    [filteredMesures]
  );

  const yDomain = useMemo(() => {
    const refs = chartRefLines(config);
    if (!chartData.length) {
      if (refs.length) {
        const rMin = Math.min(...refs.map((r) => r.value));
        const rMax = Math.max(...refs.map((r) => r.value));
        const pad = (rMax - rMin) * 0.05 || 50;
        return [rMin - pad, rMax + pad];
      }
      return config.refLine ? [0, config.refLine + 5] : [0, 1];
    }
    const vals = chartData.map((d) => d.valeur);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const refMin = refs.length ? Math.min(...refs.map((r) => r.value)) : 0;
    const refMax = refs.length ? Math.max(...refs.map((r) => r.value)) : (config.refLine ?? max);
    const yMin = Math.min(min, refMin, 0);
    const yMax = Math.max(max, refMax);
    const pad = (yMax - yMin) * 0.08 || (yMax * 0.05) || 0.01;
    return [yMin - pad, yMax + pad];
  }, [chartData, config]);

  const median = payload?.summary?.median;
  const lotsUniques = payload?.summary?.lots_uniques ?? new Set(filteredMesures.map((m) => m.lot)).size;

  return (
    <div style={S.section}>
      <div style={S.sectionHeader}>
        <div>
          <div style={S.sectionTitle}>{config.title}</div>
          <div style={S.sectionSub}>
            Courbe · {limitLabel(sliceLimit)}
            {selectedReceptionLot ? ` · PN ${selectedReceptionLot.PN}` : ""}
            {" · "}{config.columnLabel} (plyPn banc) · survolez un point
          </div>
        </div>
        <div style={S.sliceBar}>
          {SLICE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              style={{ ...S.sliceBtn, ...(sliceLimit === opt.key ? S.sliceBtnActive : {}) }}
              onClick={() => setSliceLimit(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {!loading && !error && (
        <div style={S.lotFilterBar}>
          <span style={S.filterLabel}>Lot bois (PN)</span>
          <input
            type="text"
            value={lotSearch}
            onChange={(e) => setLotSearch(e.target.value)}
            placeholder="Rechercher PN réception, plyPn…"
            style={S.lotSearch}
          />
          <select
            value={filterReceptionPn}
            onChange={(e) => setFilterReceptionPn(e.target.value)}
            style={S.lotSelect}
          >
            <option value="Tous">Tous les lots réception ({receptionLots.length})</option>
            {lotsForSelect.map((lot) => {
              const numLot = lot["Num de lot"] ? ` · n° ${lot["Num de lot"]}` : "";
              const ess = lot.Essence ? ` · ${lot.Essence}` : "";
              return (
                <option key={lot.PN} value={lot.PN}>
                  {lot.PN}{numLot}{ess}
                </option>
              );
            })}
          </select>
          {filterReceptionPn !== "Tous" && (
            <button
              type="button"
              style={S.lotClearBtn}
              onClick={() => { setFilterReceptionPn("Tous"); setLotSearch(""); }}
            >
              Effacer
            </button>
          )}
        </div>
      )}

      {loading && <div style={S.msg}>{config.loadingMsg}</div>}
      {error && <div style={{ ...S.msg, color: COLORS.red }}>{error}</div>}

      {!loading && !error && (
        <>
          <div style={S.kpiRow}>
            <KpiMini
              label="Plis affichés"
              value={filteredMesures.length}
              sub={
                selectedReceptionLot
                  ? `${selectedReceptionLot.Essence || "—"} · ${selectedReceptionLot["Num de lot"] || "—"}`
                  : `${lotsUniques} lots · ${limitLabel(sliceLimit)}`
              }
              color={COLORS.blue}
              config={config}
            />
            <KpiMini
              label="Médiane"
              value={median != null ? formatValue(median, config.decimals, config.unit) : "—"}
              sub={config.medianSub}
              color={config.lineColor}
              config={config}
            />
            <KpiMini
              label={config.alertKpiLabel}
              value={alertSummary.count ?? 0}
              sub={alertSummary.pct != null ? `${alertSummary.pct} % · survoler` : "survoler pour les plis"}
              color={(alertSummary.count ?? 0) > 0 ? COLORS.red : COLORS.green}
              hoverLots={alertSummary.top_lots}
              config={config}
            />
          </div>

          <div style={S.chartCard}>
            {chartData.length === 0 ? (
              <div style={S.msg}>
                {filterReceptionPn !== "Tous"
                  ? `Aucune mesure banc pour le PN ${filterReceptionPn}. Vérifiez qu'un plyPn (ex. ${filterReceptionPn}) existe au banc de test.`
                  : "Aucune mesure pour cette sélection."}
              </div>
            ) : (
              <>
                <div style={S.legendRow}>
                  {config.legend.map((item) => (
                    <span key={item.label} style={S.legendItem}>
                      {item.line ? (
                        <span style={{ ...S.legendLine, borderColor: item.color }} />
                      ) : (
                        <span style={{ ...S.legendDot, background: item.color }} />
                      )}
                      {item.label}
                    </span>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={380}>
                  <LineChart data={chartData} margin={{ top: 16, right: 12, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="4 4" stroke={COLORS.grid} />
                    <XAxis
                      dataKey="idx"
                      tick={{ fill: COLORS.gray, fontSize: 10 }}
                      tickFormatter={(v) => chartData[v - 1]?.axisLabel || v}
                      interval={Math.max(0, Math.floor(chartData.length / 8) - 1)}
                      label={{ value: "Plis (ordre chronologique)", position: "insideBottom", offset: -2, fill: COLORS.gray, fontSize: 11 }}
                    />
                    <YAxis
                      domain={yDomain}
                      tick={{ fill: COLORS.gray, fontSize: 11 }}
                      tickFormatter={(v) => formatValue(v, config.decimals, config.unit)}
                      label={{ value: config.yAxisLabel, angle: -90, position: "insideLeft", fill: COLORS.gray, fontSize: 11 }}
                    />
                    <Tooltip content={<MesureTooltip config={config} />} />
                    {chartRefLines(config).map((ref, index, refs) => (
                      <ReferenceLine
                        key={`${ref.value}-${ref.label}`}
                        y={ref.value}
                        stroke={ref.color || COLORS.yellow}
                        strokeDasharray="6 4"
                        strokeWidth={2}
                        ifOverflow="extendDomain"
                        label={(
                          <RefLineLabel
                            value={ref.label}
                            fill={ref.color || COLORS.yellow}
                            labelPosition={refLineLabelPosition(ref, index, refs.length)}
                          />
                        )}
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="valeur"
                      stroke={config.lineColor}
                      strokeWidth={2}
                      dot={<MesureDot config={config} />}
                      activeDot={{ r: 7, stroke: COLORS.white, strokeWidth: 2 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  section: {
    background: "rgba(255,255,255,0.025)",
    border: "1px solid #1e3a50",
    borderRadius: 14,
    padding: "20px 22px",
    backdropFilter: "blur(8px)",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  sliceBar: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: 4,
  },
  sliceBtn: {
    padding: "6px 12px",
    borderRadius: 6,
    border: "none",
    background: "transparent",
    color: COLORS.gray,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 12,
  },
  sliceBtnActive: {
    background: COLORS.yellow,
    color: "#000",
  },
  lotFilterBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 16,
    padding: "12px 14px",
    borderRadius: 10,
    background: "rgba(255,255,255,0.025)",
    border: "1px solid #1e3a50",
  },
  filterLabel: {
    fontSize: 10,
    color: COLORS.gray,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    minWidth: 28,
  },
  lotSearch: {
    flex: "1 1 180px",
    minWidth: 160,
    maxWidth: 280,
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "#041e2e",
    color: COLORS.white,
    fontSize: 12,
    outline: "none",
  },
  lotSelect: {
    flex: "2 1 260px",
    minWidth: 220,
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "#041e2e",
    color: COLORS.white,
    fontSize: 12,
    fontFamily: "monospace",
    fontWeight: 600,
    cursor: "pointer",
  },
  lotClearBtn: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "transparent",
    color: COLORS.gray,
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 700,
  },
  sectionTitle: { fontSize: 14, fontWeight: 800, color: "#e2e8f0" },
  sectionSub: { fontSize: 12, color: COLORS.gray, marginTop: 4 },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    marginBottom: 16,
  },
  kpiMini: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 10,
    padding: "12px 14px",
    borderTopWidth: 3,
    textAlign: "center",
  },
  kpiMiniValue: { fontSize: 22, fontWeight: 900 },
  kpiMiniLabel: { fontSize: 10, color: COLORS.gray, fontWeight: 700, textTransform: "uppercase", marginTop: 4 },
  kpiMiniSub: { fontSize: 10, color: "#64748b", marginTop: 2 },
  msg: { color: COLORS.gray, padding: "24px 0", textAlign: "center", fontSize: 13 },
  chartCard: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid #1e3a50",
    borderRadius: 12,
    padding: "16px 12px 8px",
  },
  legendRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 12,
    paddingLeft: 8,
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    color: COLORS.gray,
    fontWeight: 600,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
  },
  legendLine: {
    width: 18,
    height: 0,
    borderTop: "2px dashed",
    display: "inline-block",
  },
};
